import React, { useState, useEffect } from "react";
import {
  Button,
  Alert,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import Pagination from "react-bootstrap/Pagination";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { FaTrash } from "react-icons/fa";

import { useTransactions } from "../../contextjs/TransactionContext";
import { formatNairobiDateTime } from "../../utils/dateTime";
import { fetchCurrentStore, fetchUsers } from "../../api/api";

const Transactions = () => {
  const {
    transactions,
    totalCount,
    loading,
    createNewTransaction,
    accounts = [],
    accountsLoading,
    loadTransactions,
  } = useTransactions();

  // local state for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 20;
  const totalPages = Math.ceil(totalCount / entriesPerPage);

  // local date range filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [createdByFilter, setCreatedByFilter] = useState("");
  const [transactionNumberInput, setTransactionNumberInput] = useState("");
  const [referenceInput, setReferenceInput] = useState("");
  const [transactionNumberFilter, setTransactionNumberFilter] = useState("");
  const [referenceFilter, setReferenceFilter] = useState("");
  const [users, setUsers] = useState([]);
  const [currentStore, setCurrentStore] = useState(null);

  // modal states
  const [showModal, setShowModal] = useState(false);

  // transaction form data
  const [transaction, setTransaction] = useState({
    transaction_date: new Date().toISOString().split("T")[0],
    reference: "",
    description: "",
    items: [{ account: "", item_description: "", debit: 0, credit: 0 }],
  });

  // details modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const navigate = useNavigate();

  // on mount or if the currentPage changes, fetch transactions
  // possibly also watch data changes if I want auto re-fetch
  useEffect(() => {
    const filters = {};
    if (startDate) filters.start_date = startDate;
    if (endDate) filters.end_date = endDate;
    if (createdByFilter) filters.created_by = createdByFilter;
    if (transactionNumberFilter) filters.transaction_number = transactionNumberFilter;
    if (referenceFilter) filters.reference = referenceFilter;

    loadTransactions(currentPage, entriesPerPage, filters);
  }, [
      currentPage,
      startDate,
      endDate,
      createdByFilter,
      transactionNumberFilter,
      referenceFilter,
      loadTransactions,
  ]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setUsers(await fetchUsers());
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    fetchCurrentStore()
        .then(setCurrentStore)
        .catch(() => setCurrentStore(null));
  }, []);

  // Click handler to fetch from the server
  const handleFetchTransactions = () => {
    setTransactionNumberFilter(transactionNumberInput.trim());
    setReferenceFilter(referenceInput.trim());
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // transaction form item handling
  const handleItemChange = (index, value, name) => {
    const items = [...transaction.items];
    if (name === "account") {
      items[index][name] = value.value;
    } else {
      items[index][name] = value;
    }
    setTransaction({ ...transaction, items });
  };

  const handleAmountChange = (index, e) => {
    const { name, value } = e.target;
    const items = [...transaction.items];
    items[index][name] = value === "" ? "" : parseFloat(value);
    setTransaction({ ...transaction, items });
  };

  const addItem = () => {
    setTransaction({
      ...transaction,
      items: [
        ...transaction.items,
        { account: "", item_description: "", debit: 0, credit: 0 },
      ],
    });
  };

  const removeItem = (idx) => {
    const items = [...transaction.items];
    items.splice(idx, 1);
    setTransaction({ ...transaction, items });
  };

  // handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const totalDebit = transaction.items.reduce((acc, it) => acc + (it.debit || 0), 0);
    const totalCredit = transaction.items.reduce((acc, it) => acc + (it.credit || 0), 0);
    if (totalDebit !== totalCredit) {
      alert("Debit and Credit must match.");
      return;
    }

    const newTransaction = {
      transaction_date: transaction.transaction_date,
      reference: transaction.reference,
      description: transaction.description,
      items: transaction.items.map((it) => ({
        account: it.account,
        item_description: it.item_description,
        transaction_type: it.debit > 0 ? "DEBIT" : "CREDIT",
        amount: it.debit || it.credit,
      })),
    };

    try {
      await createNewTransaction(newTransaction);
    } catch (error) {
      const data = error.response?.data;
      const message = data?.items || data?.detail || "Unable to create journal transaction.";
      alert(Array.isArray(message) ? message.join("") : message);
      return;
    }

    // reset form
    setTransaction({
      transaction_date: new Date().toISOString().split("T")[0],
      reference: "",
      description: "",
      items: [{ account: "", item_description: "", debit: 0, credit: 0 }],
    });
    setShowModal(false);
  };

  const handleShowDetails = (trx) => {
    setSelectedTransaction(trx);
    setShowDetailsModal(true);
  };
  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedTransaction(null);
  };

  // account options for react-select
  const accountsOptions = accounts.map((ac) => ({
    value: ac.id,
    label: ac.account_name,
  }));

  // Bounded pagination range for display
  const maxLinks = 5;
  let startLink = Math.max(currentPage - 2, 1);
  let endLink = startLink + maxLinks - 1;
  if (endLink > totalPages) {
    endLink = totalPages;
    startLink = Math.max(endLink - (maxLinks - 1), 1);
  }
  const pageNumbersToShow = [];
  for (let p = startLink; p <= endLink; p++) {
    pageNumbersToShow.push(p);
  }

  if (loading || accountsLoading) {
    return <p>Loading...</p>;
  }

  return (
    <div>
      <div className="back-button-container">
        <Button variant="secondary" onClick={handleBack}>
          Back
        </Button>
      </div>

      <Container className="mt-5">
        <h1 className="text-center mb-4">Transactions</h1>

        <Row>
          <Col md={12} className="mb-4">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>Journal Entries</span>
                <Button variant="warning" onClick={() => setShowModal(true)}>
                  Add Entry
                </Button>
              </Card.Header>

              {/* Transaction filters (always visible) */}
              <Row className="mb-4 g-3">
                <Col md={3}>
                  <Form.Group controlId="formTransactionNumber">
                    <Form.Label>Transaction # / ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. 54269"
                      value={transactionNumberInput}
                      onChange={(e) => setTransactionNumberInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleFetchTransactions()}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="formReference">
                    <Form.Label>Reference</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. Order 2026578420"
                      value={referenceInput}
                      onChange={(e) => setReferenceInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleFetchTransactions()}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="formStartDate">
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="formEndDate">
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="formCreatedBy">
                    <Form.Label>Created By</Form.Label>
                    <Form.Select
                      value={createdByFilter}
                      onChange={(e) => setCreatedByFilter(e.target.value)}
                    >
                      <option value="">All Users</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>{u.username}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button variant="primary" onClick={handleFetchTransactions}>
                    Load Transactions
                  </Button>
                </Col>
              </Row>

              <Card.Body>
                {/* If no transactions, display a message but keep the date filters above */}
                {transactions.length === 0 ? (
                  <>
                    <h3>No transactions found</h3>
                  </>
                ) : (
                  <>
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Transaction #</th>
                          <th>Date & Time</th>
                          <th>Reference</th>
                          <th>Description</th>
                          <th>Amount</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((entry) => {
                          // If you want to show total DEBIT:
                          const totalDebit = entry.items
                            .filter((it) => it.transaction_type === "DEBIT")
                            .reduce((acc, it) => acc + parseFloat(it.amount || 0), 0);

                          return (
                            <tr key={entry.id}>
                              <td>{entry.transaction_number}</td>
                              <td>
                                {/*{new Date(entry.transaction_date).toLocaleDateString("en-GB")}*/}
                                {formatNairobiDateTime(entry.created_at || entry.transaction_date)}
                                <div className="text-muted small">
                                  By: {entry.created_by_username || "-"}
                                </div>
                              </td>
                              <td>{entry.reference}</td>
                              <td>{entry.description}</td>
                              <td>{totalDebit.toFixed(2)}</td>
                              <td>
                                <Button variant="info" onClick={() => handleShowDetails(entry)}>
                                  View Details
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>

                    {/* Local pagination */}
                    <Pagination className="justify-content-center mt-4">
                      <Pagination.First
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                      />
                      <Pagination.Prev
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      />
                      {pageNumbersToShow.map((pg) => (
                        <Pagination.Item
                          key={pg}
                          active={pg === currentPage}
                          onClick={() => handlePageChange(pg)}
                        >
                          {pg}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      />
                      <Pagination.Last
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                      />
                    </Pagination>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Add Transaction Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered>
          <Modal.Header closeButton>
            <Modal.Title>Add Transaction</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="info">
              This journal will post to: <strong>{currentStore?.name || "No active store"}</strong>
            </Alert>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={4}>
                  <Form.Group controlId="formDate">
                    <Form.Label>Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={transaction.transaction_date}
                      onChange={(e) =>
                        setTransaction({ ...transaction, transaction_date: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="formReference">
                    <Form.Label>Reference</Form.Label>
                    <Form.Control
                      type="text"
                      value={transaction.reference}
                      onChange={(e) =>
                        setTransaction({ ...transaction, reference: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="formDescription">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      type="text"
                      value={transaction.description}
                      onChange={(e) =>
                        setTransaction({ ...transaction, description: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Table striped bordered hover className="mt-3">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Particulars</th>
                    <th>Debit</th>
                    <th>Credit</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {transaction.items.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <Select
                          options={accountsOptions}
                          value={accountsOptions.find((opt) => opt.value === item.account)}
                          onChange={(selectedOption) =>
                            handleItemChange(index, selectedOption, "account")
                          }
                          isSearchable
                          placeholder="Select Account"
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          value={item.item_description}
                          onChange={(e) =>
                            handleItemChange(index, e.target.value, "item_description")
                          }
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          name="debit"
                          value={item.debit}
                          onChange={(e) => handleAmountChange(index, e)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          name="credit"
                          value={item.credit}
                          onChange={(e) => handleAmountChange(index, e)}
                        />
                      </td>
                      <td>
                        <Button variant="danger" onClick={() => removeItem(index)}>
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="4" className="text-right">
                      <strong>Total</strong>
                    </td>
                    <td>
                      {transaction.items.reduce((acc, it) => acc + (it.debit || 0), 0)}
                    </td>
                    <td>
                      {transaction.items.reduce((acc, it) => acc + (it.credit || 0), 0)}
                    </td>
                  </tr>
                </tfoot>
              </Table>

              <Button variant="success" className="mt-3" onClick={addItem}>
                + Add Line
              </Button>
              <Button
                type="submit"
                className="mt-3 float-end"
                disabled={
                  transaction.items.reduce((a, it) => a + (it.debit || 0), 0) !==
                  transaction.items.reduce((a, it) => a + (it.credit || 0), 0)
                }
              >
                Save Transaction
              </Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Transaction Details Modal */}
        <Modal show={showDetailsModal} onHide={handleCloseDetails} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Transaction Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedTransaction && (
              <>
                <h5>Transaction Number: {selectedTransaction.transaction_number}</h5>
                <h5>
                  Date & Time:{" "}
                  {/*{new Date(selectedTransaction.transaction_date).toLocaleDateString("en-GB")}*/}
                  {formatNairobiDateTime(selectedTransaction.created_at || selectedTransaction.transaction_date)}
                </h5>
                <h5>Created By: {selectedTransaction.created_by_username || "-"}</h5>
                <h5>Reference: {selectedTransaction.reference}</h5>
                <h5>Description: {selectedTransaction.description}</h5>

                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Particulars</th>
                      <th>Debit</th>
                      <th>Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTransaction.items.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.account_details?.account_name}</td>
                        <td>{item.item_description}</td>
                        <td>{item.transaction_type === "DEBIT" ? item.amount : ""}</td>
                        <td>{item.transaction_type === "CREDIT" ? item.amount : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default Transactions;
